import { useEffect, useState } from "react";
import { RiCloseFill } from "react-icons/ri";

import ModerationManualIcon from "@/assets/svg/moderation-manual.svg";
import LabelledCombobox from "@/components/combobox/LabelledCombobox";
import MissionCombobox from "@/components/combobox/MissionCombobox";
import SearchInput from "@/components/SearchInput";
import Select from "@/components/Select";
import { DOMAINS_LABELS } from "@/constants";
import STATUS, { DEPARTMENT_LABELS, JVA_MODERATION_COMMENTS_LABELS, STATUS_PLR } from "@/scenes/broadcast/moderation/components/Constants";
import api from "@/services/api";
import { captureError } from "@/services/error";
import useStore from "@/services/store";

const Filters = ({ filters, onChange, reload }) => {
  const { publisher } = useStore();
  const [options, setOptions] = useState({
    status: [],
    comments: [],
    publishers: [],
    activities: [],
    domains: [],
    departments: [],
    organizations: [],
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchOptions = async () => {
      setLoading(true);
      try {
        const query = {
          moderatorId: publisher.id || undefined,
          status: filters.status || undefined,
          comment: filters.comment || undefined,
          publisherIds: filters.publisherIds || undefined,
          cities: filters.cities || undefined,
          domain: filters.domain || undefined,
          department: filters.department || undefined,
          organizationIds: filters.organizationIds || undefined,
          activity: filters.activity || undefined,
          search: filters.search || undefined,
        };

        const res = await api.post("/moderation/aggs", query);

        if (!res.ok) {
          throw res;
        }

        setOptions(res.data);
      } catch (error) {
        captureError(error, { extra: { filters } });
      } finally {
        setLoading(false);
      }
    };
    fetchOptions();
  }, [filters, reload]);

  return (
    <div className="mx-12">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img src={ModerationManualIcon} className="h-14 w-14" alt="" aria-hidden="true" />
          <h2 className="text-xl font-semibold text-black">Modération manuelle</h2>
        </div>
      </div>
      <div className="mt-4 mb-4 flex w-full justify-start sm:mt-0">
        <SearchInput
          label="Rechercher une mission"
          value={filters.search}
          onChange={(e) => onChange({ ...filters, search: e })}
          placeholder="Rechercher"
          className="w-full sm:w-[40%]"
        />
      </div>
      <div className="grid grid-cols-1 gap-4 pb-4 md:grid-cols-2 lg:grid-cols-4">
        <Select
          options={options.status.map((e) => ({ value: e.key, label: STATUS_PLR[e.key], count: e.doc_count }))}
          value={filters.status}
          onChange={(e) => onChange({ ...filters, status: e.value })}
          placeholder="Statut"
          loading={loading}
        />

        <LabelledCombobox
          id="publisherIds"
          values={filters.publisherIds}
          onChange={(publisherIds) => onChange({ ...filters, publisherIds })}
          placeholder="Annonceur"
          options={options.publishers}
        />
        <Select
          options={options.domains.map((e) => ({ value: e.key === "" ? "none" : e.key, label: e.key === "" ? "Non renseignée" : DOMAINS_LABELS[e.key], count: e.doc_count }))}
          value={filters.domain}
          onChange={(e) => onChange({ ...filters, domain: e.value })}
          placeholder="Domaine"
          loading={loading}
        />

        <LabelledCombobox
          id="organizationIds"
          values={filters.organizationIds}
          onChange={(organizationIds) => onChange({ ...filters, organizationIds })}
          placeholder="Organisation"
          options={options.organizations}
        />

        <Select
          options={options.comments.map((e) => ({ value: e.key, label: JVA_MODERATION_COMMENTS_LABELS[e.key] || e.key, count: e.doc_count }))}
          value={filters.comment}
          onChange={(e) => onChange({ ...filters, comment: e.value })}
          placeholder="Motif de refus"
          loading={loading}
        />
        <Select
          options={options.departments.map((e) => ({
            value: e.key === "" ? "none" : e.key,
            label: e.key === "" ? "Non renseigné" : DEPARTMENT_LABELS[e.key],
            count: e.doc_count,
          }))}
          value={filters.department}
          onChange={(e) => onChange({ ...filters, department: e.value })}
          placeholder="Département"
          loading={loading}
        />

        <MissionCombobox
          id="city"
          values={filters.cities}
          onChange={(cities) => onChange({ ...filters, cities })}
          placeholder="Villes"
          filters={`${filters.publisherIds?.length ? filters.publisherIds.map((p) => `publishers=${p}`).join("&") : options.publishers.map((p) => `publishers=${p.key}`).join("&")}&field=city`}
        />
        <Select
          options={options.activities.map((e) => ({ value: e.key === "" ? "none" : e.key, label: e.key === "" ? "Non renseignée" : e.key, count: e.doc_count }))}
          value={filters.activity}
          onChange={(e) => onChange({ ...filters, activity: e.value })}
          placeholder="Activité"
          loading={loading}
        />
      </div>

      <div className="flex flex-wrap gap-3">
        <Badge label="Statut" value={STATUS[filters.status]} onDelete={() => onChange({ ...filters, status: "" })} />
        <Badge
          label="Annonceur"
          value={
            filters.publisherIds.length > 0
              ? options.publishers
                  .filter((p) => filters.publisherIds.includes(p.key))
                  .map((p) => p.label)
                  .join(", ")
              : null
          }
          onDelete={() => onChange({ ...filters, publisherIds: [] })}
        />
        <Badge
          label="Organisation"
          value={
            filters.organizationIds.length > 0
              ? options.organizations
                  .filter((o) => filters.organizationIds.includes(o.key))
                  .map((o) => o.label)
                  .join(", ")
              : null
          }
          onDelete={() => onChange({ ...filters, organizationIds: [] })}
        />
        <Badge
          label="Département"
          value={filters.department === "none" ? "Non renseigné" : DEPARTMENT_LABELS[filters.department]}
          onDelete={() => onChange({ ...filters, department: "" })}
        />
        <Badge
          label="Ville"
          value={
            filters.cities.length > 0
              ? options.cities
                  .filter((c) => filters.cities.includes(c.key))
                  .map((c) => (c.key === "none" ? "Non renseignée" : c.key))
                  .join(", ")
              : null
          }
          onDelete={() => onChange({ ...filters, cities: [] })}
        />
        <Badge label="Motif de refus" value={JVA_MODERATION_COMMENTS_LABELS[filters.comment] || filters.comment} onDelete={() => onChange({ ...filters, comment: "" })} />
        <Badge label="Domaine" value={filters.domain === "none" ? "Non renseigné" : DOMAINS_LABELS[filters.domain]} onDelete={() => onChange({ ...filters, domain: "" })} />
        <Badge label="Activité" value={filters.activity === "none" ? "Non renseignée" : filters.activity} onDelete={() => onChange({ ...filters, activity: "" })} />
        <Badge label="Recherche" value={filters.search} onDelete={() => onChange({ ...filters, search: "" })} />
      </div>
    </div>
  );
};

const Badge = ({ label, value, onDelete }) => {
  if (!value) {
    return null;
  }
  return (
    <button type="button" className="bg-blue-france-975 flex items-center gap-2 rounded p-2" title={`${label} : ${value} - Supprimer`} onClick={onDelete}>
      <span className="text-sm">{label} :</span>
      <span className="text-sm">{value}</span>
      <RiCloseFill className="text-sm text-black" aria-hidden="true" />
    </button>
  );
};

export default Filters;
